import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Lock, LockOpen, CheckCircle, Play } from "@phosphor-icons/react";
import { PlatePhotos } from "@/components/CuttingPlatesSection";
import { pickPlateItem, n3 } from "@/lib/gate";

const EDITABLE = ["sheet_size_pending", "design_making", "design_pending"];
const sq = (r) => ((Number(r.width_in) || 0) * (Number(r.height_in) || 0) * (Number(r.quantity) || 0)) / 144;
const inp = "min-w-0 w-full h-9 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num disabled:opacity-60";

export const workItemsOf = (order) => (order.work_items?.length ? order.work_items
  : (order.items || []).filter((i) => i.work_item).map((i) => ({ ...i.work_item, item_name: i.item_name })));

// Stage buttons for gates v2. mode "fab" = fabricator (no cutting-done).
export function GateStageActions({ order, mode, onChanged }) {
  const base = mode === "fab" ? `/fabrication/orders/${order.id}` : `/orders/${order.id}`;
  const st = order.raw_status || order.status;
  const acts = {
    new_order: ["start_framing", "START FRAMING", "Framing shuru karein?"],
    framing_making: ["framing_done", "FRAMING DONE", "Framing done? Agla step: UPLOAD SHEET SIZE."],
    ...(mode === "fab" ? {} : {
      in_cutting: ["cutting_done", "CUTTING DONE", "Cutting done? Order Ready for Installation me jayega."],
      design_cutting: ["cutting_done", "CUTTING DONE", "Cutting done? Order Ready for Installation me jayega."],
    }),
    ready_for_installation: ["installation_done", "INSTALLATION DONE", "Installation done? Order Ready for Delivery me jayega."],
  };
  const a = acts[st];
  if (!a) return null;
  const go = async () => {
    if (!window.confirm(a[2])) return;
    try { await api.post(`${base}/gate/${a[0]}`); toast.success(`${a[1]} ✓`); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <button onClick={go} className="h-10 px-4 rounded-sm bg-violet-500 hover:bg-violet-400 text-white text-xs font-heading tracking-widest flex items-center gap-1" data-testid={`gate-action-${a[0]}`}>
      <Play size={14} weight="fill" /> {a[1]}
    </button>
  );
}

export default function GateSheetSizes({ order, mode, onChanged, canUnlock }) {
  const base = mode === "fab" ? `/fabrication/orders/${order.id}` : `/orders/${order.id}`;
  const st = order.raw_status || order.status;
  const wis = useMemo(() => workItemsOf(order), [order]);
  const [plateItems, setPlateItems] = useState([]);
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const notOpen = ["new_order", "framing_making"].includes(st);
  const locked = !EDITABLE.includes(st) && !order.plates_unlocked;

  useEffect(() => { api.get("/plate-items").then(({ data }) => setPlateItems(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const m = {};
    (order.cutting_plates || []).forEach((p) => {
      (m[p.work_item_id] = m[p.work_item_id] || []).push({ item_id: p.item_id, width_in: p.width_in, height_in: p.height_in, quantity: p.quantity });
    });
    setRows(m);
  }, [order.id, order.cutting_plates]);

  const addRow = (wi) => setRows((r) => ({ ...r, [wi.id]: [...(r[wi.id] || []), { item_id: pickPlateItem(plateItems, wi.thickness_mm), width_in: "", height_in: "", quantity: 1 }] }));
  const setRow = (wid, i, k, v) => setRows((r) => ({ ...r, [wid]: r[wid].map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const delRow = (wid, i) => setRows((r) => ({ ...r, [wid]: r[wid].filter((_, j) => j !== i) }));
  const total = Object.values(rows).flat().reduce((s, r) => s + sq(r), 0);

  const save = async () => {
    const all = Object.values(rows).flat();
    if (!all.length) { toast.error("Kam se kam ek plate size daaliye"); return; }
    if (all.some((r) => !r.item_id || !(Number(r.width_in) > 0) || !(Number(r.height_in) > 0))) { toast.error("Har row me plate, W aur H bhariye"); return; }
    if (!window.confirm(`SAVE SHEET SIZES? Total ${n3(total)} sqft — stock sirf difference se adjust hoga.`)) return;
    setBusy(true);
    try {
      await api.put(`${base}/sheet-sizes`, { work_items: wis.map((wi) => ({ work_item_id: wi.id,
        plates: (rows[wi.id] || []).map((r) => ({ item_id: r.item_id, width_in: Number(r.width_in), height_in: Number(r.height_in), quantity: Number(r.quantity) || 1 })) })) });
      toast.success("Sheet sizes saved ✓"); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unlock = async () => {
    if (!window.confirm("Sheet sizes UNLOCK karein? Change pe stock difference se adjust hoga.")) return;
    try { await api.post(`/orders/${order.id}/cutting-plates/unlock`); toast.success("Unlocked"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="surface p-4 rounded-sm border-l-4 border-cyan-500 space-y-3" data-testid="gate-sheet-sizes">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">// UPLOAD SHEET SIZE — har work item ki plates</div>
          <div className="text-xs text-slate-400">Sirf Shobhit Creation staff / fabricator bharega. Stock save karte hi katega.</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <GateStageActions order={order} mode={mode} onChanged={onChanged} />
          {locked && !notOpen && <span className="flex items-center gap-1 text-[10px] font-mono-num text-amber-300" data-testid="sheet-sizes-locked"><Lock size={12} /> LOCKED</span>}
          {locked && !notOpen && canUnlock && (
            <button onClick={unlock} className="h-8 px-3 border border-amber-500 text-amber-200 rounded-sm text-[10px] font-mono-num flex items-center gap-1" data-testid="sheet-sizes-unlock-btn"><LockOpen size={12} /> UNLOCK</button>
          )}
        </div>
      </div>
      {notOpen ? (
        <div className="text-sm text-slate-400 italic flex items-center gap-2" data-testid="sheet-sizes-not-open"><Lock size={14} /> FRAMING DONE ke baad khulega</div>
      ) : (
        <>
          {wis.map((wi) => (
            <div key={wi.id} className="surface-2 p-3 rounded-sm space-y-2" data-testid={`sheet-wi-${wi.id}`}>
              <div className="text-xs font-heading tracking-widest text-slate-200">{wi.item_name || wi.label}</div>
              {(rows[wi.id] || []).map((r, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,0.8fr))_minmax(0,0.8fr)_auto] gap-2 items-center" data-testid={`sheet-row-${wi.id}-${i}`}>
                  <select value={r.item_id} disabled={locked} onChange={(e) => setRow(wi.id, i, "item_id", e.target.value)} className={`${inp} col-span-2 md:col-span-1`} data-testid={`sheet-item-${wi.id}-${i}`}>
                    <option value="">Plate…</option>
                    {plateItems.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <input type="number" min="0" placeholder="W inch" value={r.width_in} disabled={locked} onChange={(e) => setRow(wi.id, i, "width_in", e.target.value)} className={inp} data-testid={`sheet-w-${wi.id}-${i}`} />
                  <input type="number" min="0" placeholder="H inch" value={r.height_in} disabled={locked} onChange={(e) => setRow(wi.id, i, "height_in", e.target.value)} className={inp} data-testid={`sheet-h-${wi.id}-${i}`} />
                  <input type="number" min="1" placeholder="Qty" value={r.quantity} disabled={locked} onChange={(e) => setRow(wi.id, i, "quantity", e.target.value)} className={inp} data-testid={`sheet-qty-${wi.id}-${i}`} />
                  <div className="font-mono-num text-cyan-300 text-sm">{n3(sq(r))} sqft</div>
                  {!locked ? <button onClick={() => delRow(wi.id, i)} className="text-red-400 p-1" data-testid={`sheet-del-${wi.id}-${i}`}><Trash size={16} /></button> : <span />}
                </div>
              ))}
              {!locked && (
                <button onClick={() => addRow(wi)} className="h-8 px-3 border border-cyan-500/60 text-cyan-200 rounded-sm text-[10px] font-mono-num flex items-center gap-1" data-testid={`sheet-add-${wi.id}`}><Plus size={12} /> ADD PLATE</button>
              )}
            </div>
          ))}
          <div className="flex items-center gap-2 flex-wrap">
            {!locked && (
              <button onClick={save} disabled={busy} className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-heading tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid="save-sheet-sizes-btn"><CheckCircle size={16} weight="fill" /> SAVE SHEET SIZES</button>
            )}
            <span className="ml-auto text-sm text-slate-300">Total plate: <b className="font-mono-num text-cyan-300" data-testid="sheet-sizes-total">{n3(total)} sqft</b></span>
          </div>
          <PlatePhotos order={order} mode={mode} locked={locked} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}
